import React, { useEffect, useMemo, useState } from "react";
import { FiDelete } from "react-icons/fi";
import styles from "./DiscountModal.module.css";

const onlyDigits = (s) => (s || "").replace(/[^\d]/g, "");
const onlyDigitsAndDot = (s) => (s || "").replace(/[^\d.]/g, "");
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const formatMoney = (n) => Number(n || 0).toFixed(2);

export default function DiscountModal({ open, item, onClose, onSave }) {
  const [qty, setQty] = useState(1);
  const [discountStr, setDiscountStr] = useState("0");
  const [mode, setMode] = useState("percent"); // 'percent' | 'amount'
  const [cursor, setCursor] = useState("discount"); // 'qty' | 'discount'

  useEffect(() => {
    if (!open) return;

    setCursor("discount");
    setQty(item?.qty || 1);

    const d = item?.discount;
    if (d?.type) {
      setMode(d.type);
      setDiscountStr(String(d.value ?? "0"));
    } else {
      setMode("percent");
      setDiscountStr("0");
    }
  }, [open, item]);

  const unitPrice = Number(item?.price || 0);

  const discountValue = useMemo(() => {
    const v = Number(onlyDigitsAndDot(discountStr) || 0);
    return Number.isFinite(v) ? v : 0;
  }, [discountStr]);

  const qtySafe = useMemo(() => {
    const q = Number(qty || 1);
    if (!Number.isFinite(q) || q <= 0) return 1;
    return clamp(q, 1, Number(item?.qty || 1));
  }, [qty, item]);

  const totalBefore = unitPrice * qtySafe;

  const totalDiscount = useMemo(() => {
    if (mode === "percent") {
      const pct = clamp(discountValue, 0, 100);
      return (unitPrice * pct) / 100 * qtySafe;
    }
    const perUnit = clamp(discountValue, 0, unitPrice);
    return perUnit * qtySafe;
  }, [mode, discountValue, unitPrice, qtySafe]);

  const totalAfter = Math.max(0, totalBefore - totalDiscount);

  const setQtyFromStr = (s) => {
    const d = Number(onlyDigits(s) || 1);
    setQty(clamp(d, 1, Number(item?.qty || 1)));
  };

  const setDiscountFromStr = (s) => {
    if (mode === "amount") setDiscountStr(onlyDigitsAndDot(s));
    else setDiscountStr(onlyDigits(s));
  };

  const handleKey = (key) => {
    if (cursor === "qty") {
      if (key === "back") {
        const current = String(qtySafe);
        const next = current.length <= 1 ? "1" : current.slice(0, -1);
        setQtyFromStr(next);
        return;
      }
      const next = String(qtySafe === 1 ? "" : qtySafe) + key;
      setQtyFromStr(next);
      return;
    }

    // discount
    if (key === "back") {
      const next = discountStr.length <= 1 ? "0" : discountStr.slice(0, -1);
      setDiscountFromStr(next);
      return;
    }

    if (key === "." && mode !== "amount") return;

    const base = discountStr === "0" ? "" : discountStr;
    setDiscountFromStr(base + key);
  };

  const handleSave = () => {
    if (!item?.id) return;

    let value = discountValue;
    if (mode === "percent") value = clamp(value, 0, 100);
    if (mode === "amount") value = clamp(value, 0, unitPrice);

    onSave({
      itemId: item.id,
      discount: { type: mode, value, qty: qtySafe },
    });
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.title} title={item?.name || ""}>
          {item?.name || "Товар"}
        </div>

        <div className={styles.row}>
          <div className={styles.inputWrap} onClick={() => setCursor("qty")}>
            <input
              className={`${styles.input} ${cursor === "qty" ? styles.active : ""}`}
              value={`${qtySafe} шт`}
              readOnly
            />
          </div>
        </div>

        <div className={styles.row}>
          <select className={styles.select} defaultValue="">
            <option value="" disabled>
              Выберите из списка
            </option>
            <option value="manual">Ручная скидка</option>
          </select>
        </div>

        <div className={styles.row}>
          <div className={styles.discountLine}>
            <div
              className={styles.discountInputWrap}
              onClick={() => setCursor("discount")}
            >
              <input
                className={`${styles.input} ${cursor === "discount" ? styles.active : ""}`}
                value={
                  mode === "percent"
                    ? `${Number(onlyDigits(discountStr) || 0)}`
                    : `${onlyDigitsAndDot(discountStr) || "0"}`
                }
                readOnly
              />
              <span className={styles.hint}>Скидка за ед.</span>
            </div>

            <button
              type="button"
              className={`${styles.toggle} ${mode === "percent" ? styles.toggleActive : ""}`}
              onClick={() => {
                setMode("percent");
                setDiscountStr(onlyDigits(discountStr) || "0");
              }}
              title="%"
            >
              %
            </button>

            <button
              type="button"
              className={`${styles.toggle} ${mode === "amount" ? styles.toggleActive : ""}`}
              onClick={() => setMode("amount")}
              title="₸"
            >
              ₸
            </button>
          </div>

          <div className={styles.preview}>
            <div className={styles.previewRow}>
              <span>Было:</span>
              <span className={styles.strike}>{formatMoney(totalBefore)} ₸</span>
            </div>
            <div className={styles.previewRow}>
              <span>Скидка:</span>
              <span className={styles.green}>-{formatMoney(totalDiscount)} ₸</span>
            </div>
            <div className={styles.previewRowStrong}>
              <span>Стало:</span>
              <span>{formatMoney(totalAfter)} ₸</span>
            </div>
          </div>
        </div>

        <div className={styles.pad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                key={n}
                className={styles.padBtn}
                onClick={() => handleKey(String(n))}
                >
                {n}
                </button>
            ))}

            <button className={styles.padBtn} onClick={() => handleKey("0")}>
                0
            </button>

            {/* точка всегда на своём месте (рядом с 0), но активна только в amount */}
            <button
                className={styles.padBtn}
                onClick={() => mode === "amount" && handleKey(".")}
                disabled={mode !== "amount"}
                aria-label="Точка"
                title="."
            >
                .
            </button>

            <button
                className={styles.padBtn}
                onClick={() => handleKey("back")}
                aria-label="Удалить символ"
                title="Удалить"
            >
                <FiDelete size={18} />
            </button>
            </div>


        <div className={styles.actions}>
          <button className={styles.save} onClick={handleSave}>
            Сохранить
          </button>
          <button className={styles.cancel} onClick={onClose}>
            Отменить
          </button>
        </div>
      </div>
    </div>
  );
}
